import type { Command } from '@fabricos/protocol-ts';
import { sendCommand } from '@/hooks/useHubSocket';
import { armConfirm } from '@/lib/armConfirm';
import { log } from '@/lib/logger';
import { fmtMeters } from '@/lib/utils';
import { useFleet } from '@/store/fleet';
import { useGotoTargets, distMeters, type GotoTarget } from '@/store/gotoTargets';

export interface SendGotoOpts {
  /** Append to the queue instead of replacing the active target. */
  append?: boolean;
  /** Override altitude (m AGL). Defaults to the store's `defaultAltAglM`. */
  altOverrideM?: number;
  /** Skip the far-target confirm even if dist > threshold. */
  skipConfirm?: boolean;
}

/**
 * Send a GOTO to a single robot at (lat, lon). Single source of truth for
 * the click handler, the right-click menu, and the history-resend path.
 *
 * Applies operator settings from `gotoTargets.settings`:
 *   - altitude defaults to `defaultAltAglM` unless overridden,
 *   - far-target confirm fires above `confirmRadiusM` from the drone's
 *     current position,
 *   - geofence radius (around home) is *checked* and surfaced as a label,
 *     but the marker render decides whether to draw the red ring — we never
 *     hard-block here, so the operator can override.
 *
 * Returns the actual `GotoTarget` written to the store, or `null` if the
 * send was vetoed (no selection / no GPS / user cancelled the confirm).
 */
export function sendGoto(
  robotId: string,
  lat: number,
  lon: number,
  opts: SendGotoOpts = {},
): GotoTarget | null {
  const goto = useGotoTargets.getState();
  const state = useFleet.getState().states[robotId];
  const altM = opts.altOverrideM ?? goto.settings.defaultAltAglM;

  // Far-target confirm — non-blocking arm pattern (never window.confirm: a
  // browser modal over a live map freezes flight controls). First far click
  // raises a toast; clicking again inside the window sends.
  //
  // The reference point is the end of the chain the new leg extends: for a
  // queued target that's the queue tail (or the active target), NOT the
  // drone — otherwise every shift+click while flying leg 1 reads as "far"
  // and gets vetoed, which broke queueing for the operator (2026-07-14).
  // Leg origin: chain tail for a queued leg, else the drone's position now.
  // Doubles as the far-confirm reference and the label's progress baseline.
  const chainTail = opts.append
    ? (goto.queues[robotId]?.at(-1) ?? goto.targets[robotId] ?? null)
    : null;
  const origin = chainTail ?? state?.position ?? null;

  if (!opts.skipConfirm && goto.settings.confirmRadiusM > 0) {
    const ref = origin;
    const d = ref ? distMeters(ref, { lat, lon }) : 0;
    if (
      d > goto.settings.confirmRadiusM &&
      !armConfirm(
        `far-goto-${robotId}`,
        `Far target: ${fmtMeters(d)} leg — ${opts.append ? 'ctrl+click' : 'click'} the spot again to ${opts.append ? 'queue' : 'send'}`,
      )
    ) {
      log.action('GOTO armed pending far-target confirm', { robotId, distM: d });
      return null;
    }
  }

  const target: GotoTarget = {
    robotId,
    lat,
    lon,
    alt_m: altM,
    ts_ms: Date.now(),
    ...(origin ? { from: { lat: origin.lat, lon: origin.lon } } : {}),
  };

  // Queue semantics: while a GOTO is already active, an appended target
  // must NOT reach the firmware yet — the drone finishes the current leg
  // first, and the arrival watcher promotes the queue head via
  // `sendGotoFirmware`. Only a target that becomes ACTIVE flies now.
  const queuedBehindActive = Boolean(opts.append) && goto.targets[robotId] != null;
  if (opts.append) goto.append(target);
  else goto.set(target);

  if (!queuedBehindActive) {
    const cmd: Command = {
      kind: 'goto',
      id: makeId(),
      robot_id: robotId,
      priority: 'normal',
      lat,
      lon,
      alt_m: altM,
      speed_ms: null,
      yaw_rad: null,
    };
    sendCommand(cmd);
  }

  log.action(queuedBehindActive ? 'GOTO queued' : 'GOTO sent', {
    robotId,
    lat,
    lon,
    altM,
  });
  return target;
}

/**
 * Returns whether a point lies outside the soft geofence around a robot's
 * home (drawn from RobotState). Used by the marker renderer to flag a red
 * ring and by callers that want to surface a warning before sending.
 */
export function isOutsideFence(
  robotId: string,
  pt: { lat: number; lon: number },
): boolean {
  const goto = useGotoTargets.getState();
  if (goto.settings.fenceRadiusM <= 0) return false;
  const state = useFleet.getState().states[robotId];
  if (!state?.home) return false;
  return distMeters(state.home, pt) > goto.settings.fenceRadiusM;
}

function makeId(): string {
  return `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

/** Fire the firmware GOTO command for a target whose store entry is already
 *  set (e.g. by `advance()` after the operator reached the previous one).
 *  Doesn't touch the store. */
export function sendGotoFirmware(t: GotoTarget): void {
  const cmd: Command = {
    kind: 'goto',
    id: makeId(),
    robot_id: t.robotId,
    priority: 'normal',
    lat: t.lat,
    lon: t.lon,
    alt_m: t.alt_m,
    speed_ms: null,
    yaw_rad: null,
  };
  sendCommand(cmd);
  log.action('GOTO promoted from queue', {
    robotId: t.robotId,
    lat: t.lat,
    lon: t.lon,
  });
}
